/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import {
    type MutableRefObject,
    type RefCallback,
    type RefObject,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { Logger } from '@gitbook/util-debug';
import { useLazyRef, useLiveRef } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { findScrollParent } from './utils';

const logger = Logger('react:virtual');

/** Default height of content to render (in px) */
const DEFAULT_VIRTUAL_WINDOW = Math.min(
    typeof window !== 'undefined' ? window.innerHeight : 1024,
    600
);
/** Margin for the viewport (extra space to consider in the viewport) */
const DEFAULT_VIEWPORT_MARGIN = '25%';

/** Space to render on the edge of the viewport */
const EDGE_VIRTUAL_WINDOW = DEFAULT_VIRTUAL_WINDOW / 4;

/** Default height for nodes when none can be computed by estimateNodeSize */
const NODE_DEFAULT_HEIGHT = 50;

type VirtualizationWindowChuck = Set<number | string>;

export type VirtualizationItemInput<D extends Record<string, unknown> = Record<string, unknown>> =
    D & {
        key: number | string;
    };

export type VirtualizationItem<D extends Record<string, unknown> = Record<string, unknown>> = D & {
    key: number | string;
    height: number;
    nodeRef: RefCallback<Element>;
};

export interface useVirtualizedListAttrs<D extends Record<string, unknown>> {
    enabled?: boolean;
    parentRef?: RefObject<Element | null>;
    getEstimatedItemHeight: (item: VirtualizationItemInput<D>) => number | undefined;
    list: VirtualizationItemInput<D>[];
    edgeVirtualWindow?: number;
    viewportMargin?: string;
}

export function useVirtualizedList<D extends Record<string, unknown>>({
    enabled = true,
    parentRef,
    getEstimatedItemHeight,
    list,
    edgeVirtualWindow = EDGE_VIRTUAL_WINDOW,
    viewportMargin = DEFAULT_VIEWPORT_MARGIN,
}: useVirtualizedListAttrs<D>): [VirtualizationItem<D>, boolean, number][] {
    /** Cache of dom nodes to list nodes */
    const [domToNodeMap] = useState(() => new Map<Element, VirtualizationItem<D>>());
    /** Cache height measurements to reduce layout thrashing */
    const measuredHeightsRef = useLazyRef(() => new Map<number | string, number>());
    /** Cache stable "loaded" measurements so loading fallbacks do not shrink already measured content */
    const loadedHeightsRef = useLazyRef(() => new Map<number | string, number>());

    /** Intersection observer */
    const observerRef = useRef<IntersectionObserver | null>(null);

    const elementsRefs = useRef<Array<Element | null>>([]);

    useLayoutEffect(() => {
        elementsRefs.current = elementsRefs.current.slice(0, list.length);
        const validKeys = new Set(list.map((item) => item.key));
        for (const key of Array.from(measuredHeightsRef.current.keys())) {
            if (!validKeys.has(key)) {
                measuredHeightsRef.current.delete(key);
            }
        }
        for (const key of Array.from(loadedHeightsRef.current.keys())) {
            if (!validKeys.has(key)) {
                loadedHeightsRef.current.delete(key);
            }
        }
    }, [list, measuredHeightsRef, loadedHeightsRef]);

    useEffect(() => {
        elementsRefs.current = elementsRefs.current.slice(0, list.length);
    }, [list.length]);

    const [chunk, setChunk] = useState<VirtualizationWindowChuck>(() => {
        return computeChunkForSpace(
            getEstimatedItemHeight,
            DEFAULT_VIRTUAL_WINDOW,
            list,
            measuredHeightsRef
        );
    });

    const chunkRef = useLiveRef(chunk);

    // Start observing intersection changes to update the chunk of nodes to render.
    useEffect(() => {
        if (!enabled || !parentRef?.current) {
            observerRef.current = null;
            return;
        }

        let raf: number | null = null;

        const scrollParent = findScrollParent(parentRef.current);

        const observer = new IntersectionObserver(
            (entries) => {
                raf = requestAnimationFrame(() => {
                    const newChunk = updateVisibleChunk(
                        entries,
                        chunkRef.current,
                        domToNodeMap,
                        measuredHeightsRef,
                        loadedHeightsRef
                    );
                    if (newChunk) {
                        setChunk(newChunk);
                    }
                });
            },
            {
                root: scrollParent,
                rootMargin: `${viewportMargin} 0px ${viewportMargin} 0px`,
            }
        );
        observerRef.current = observer;

        return () => {
            if (raf) {
                cancelAnimationFrame(raf);
            }
            observer.disconnect();
            observerRef.current = null;
        };
    }, [
        chunkRef,
        domToNodeMap,
        enabled,
        loadedHeightsRef,
        measuredHeightsRef,
        parentRef,
        viewportMargin,
    ]);

    const observer = observerRef.current;

    // Observe elements when the list changes
    useEffect(() => {
        if (observer) {
            const observed: Element[] = [];

            for (let i = 0; i < list.length; i++) {
                const ref = elementsRefs.current[i];
                if (!ref) {
                    return;
                }

                observed.push(ref);
                observer.observe(ref);
            }

            return () => {
                observed.forEach((element) => {
                    observer.unobserve(element);
                });
            };
        }
    }, [list, observer]);

    return computeVisibleNodes<D>(
        enabled,
        edgeVirtualWindow,
        getEstimatedItemHeight,
        chunk,
        list,
        elementsRefs,
        domToNodeMap,
        measuredHeightsRef
    );
}

/**
 * Compute a set of nodes that can fill a given height.
 * It can return more nodes than the given height if the last node is not fully visible.
 */
function computeChunkForSpace<D extends Record<string, unknown>>(
    getEstimatedItemHeight: (item: VirtualizationItemInput<D>) => number | undefined,
    distancePx: number,
    virtualNodes: VirtualizationItemInput<D>[],
    measuredHeightsRef: MutableRefObject<Map<number | string, number>>,
    position?: {
        node: string;
        direction: 'before' | 'after';
    }
): VirtualizationWindowChuck {
    let nodes = virtualNodes;
    const measuredHeights = measuredHeightsRef.current;
    if (position) {
        if (position.direction === 'before') {
            nodes = nodes.reverse();
        }

        const skippedNodes: VirtualizationItemInput<D>[] = [];
        let skip = true;
        nodes.forEach((node) => {
            if (!skip) {
                skippedNodes.push(node);
            } else if (node.key === position.node) {
                skip = false;
            }
        });

        nodes = skippedNodes;
    }

    const chunk = new Set<string | number>();
    let left = distancePx;

    for (let index = 0; index < nodes.length; index++) {
        const node = nodes[index];
        assertIsDefined(node);
        const height =
            measuredHeights.get(node.key) || getEstimatedItemHeight(node) || NODE_DEFAULT_HEIGHT;

        chunk.add(node.key);
        left = left - height;
        if (left < 0) {
            break; // we've filled the number of nodes, and must exit early
        }
    }

    return chunk;
}

/**
 * Update the set of visible nodes when getting updates from the IntersectionObserver.
 */
function updateVisibleChunk(
    entries: IntersectionObserverEntry[],
    currentChunk: VirtualizationWindowChuck,
    domToNodeMap: WeakMap<Element, VirtualizationItem>,
    measuredHeightsRef: MutableRefObject<Map<number | string, number>>,
    loadedHeightsRef: MutableRefObject<Map<number | string, number>>
): VirtualizationWindowChuck | undefined {
    const result = new Set(currentChunk);
    const measuredHeights = measuredHeightsRef.current;
    const loadedHeights = loadedHeightsRef.current;

    for (const entry of entries) {
        const node = domToNodeMap.get(entry.target);
        if (!node) {
            logger.warning('Element', entry.target, 'not found in the reverse lookup.');
            continue;
        }

        if (!entry.rootBounds) continue;

        const { top, bottom } = entry.boundingClientRect;
        const { top: rTop, bottom: rBottom } = entry.rootBounds;
        const verticallyVisible = bottom >= rTop && top <= rBottom;

        if (verticallyVisible) result.add(node.key);
        else result.delete(node.key);

        const measuredHeight = entry.boundingClientRect.height;
        if (!Number.isNaN(measuredHeight)) {
            const isVirtualPlaceholder = isVirtualizedPlaceholder(entry.target);
            const isLoading = isVirtualContentLoading(entry.target);
            const hasLoadedContent = hasVirtualContentLoaded(entry.target);
            const loadedHeight = loadedHeights.get(node.key);
            const nextHeight =
                loadedHeight !== undefined &&
                (isLoading || isVirtualPlaceholder || hasLoadedContent)
                    ? Math.max(loadedHeight, measuredHeight)
                    : measuredHeight;

            measuredHeights.set(node.key, nextHeight);
            if (!isLoading && hasLoadedContent && !isVirtualPlaceholder) {
                loadedHeights.set(node.key, measuredHeight);
            }

            if (currentChunk.has(node.key)) {
                node.height = nextHeight;
            }
        }
    }

    return areSetEquals(result, currentChunk) ? undefined : result;
}

/**
 * From the set of nodes visible in the viewport, we compute the list of nodes to actually render.
 * It ensures:
 *   - Visible chunk is continious (no gaps)
 *   - Nodes on the edge on the viewport are always rendered (EDGE_VIRTUAL_WINDOW)
 */
function computeVisibleNodes<D extends Record<string, unknown>>(
    canVirtualize: boolean,
    edgeVirtualWindow: number,
    getEstimatedItemHeight: (item: VirtualizationItemInput<D>) => number | undefined,
    viewportChunk: VirtualizationWindowChuck,
    list: VirtualizationItemInput<D>[],
    refs: MutableRefObject<Array<Element | null>>,
    domToNodeMap: WeakMap<Element, VirtualizationItem>,
    measuredHeightsRef: MutableRefObject<Map<number | string, number>>
): Array<[VirtualizationItem<D>, boolean, number]> {
    let firstVisibleNode = -1;
    let lastVisibleNode = -1;
    const measuredHeights = measuredHeightsRef.current;

    const result = list
        .map<[VirtualizationItem<D>, boolean, number]>((node, index) => {
            const nodeIsInViewport = viewportChunk.has(node.key);

            if (nodeIsInViewport) {
                if (firstVisibleNode < 0) {
                    firstVisibleNode = index;
                }
                if (index > lastVisibleNode) {
                    lastVisibleNode = index;
                }
            }

            const actualNodeHeight = measuredHeights.get(node.key);
            const nodeHeight =
                actualNodeHeight || getEstimatedItemHeight(node) || NODE_DEFAULT_HEIGHT;

            // If we have an actual node height (meaning the node has been rendered) and it's different
            // to the estimated one, log it to the console so we can keep track in dev mode.
            if (
                typeof window !== 'undefined' &&
                process.env.NODE_ENV === 'development' &&
                actualNodeHeight !== undefined
            ) {
                const estimatedNodeHeight = getEstimatedItemHeight(node);

                if (
                    estimatedNodeHeight !== undefined &&
                    Math.abs(estimatedNodeHeight - actualNodeHeight) > 2 // don't log any off-by-one errors to the console
                ) {
                    const n = (node?.node || null) as { object: string; type: string } | null;

                    logger.info(
                        `node ${node.key} of type ${
                            n ? (n.object === 'block' ? n.type : n.object) : 'no-type?'
                        } actual height: ${actualNodeHeight} estimated height: ${estimatedNodeHeight}`
                    );
                }
            }

            const virtualizedNode: VirtualizationItem<D> = {
                ...node,
                height: nodeHeight,
                nodeRef(element) {
                    if (!refs.current || !element) {
                        return;
                    }
                    domToNodeMap.set(element, virtualizedNode);
                    refs.current[index] = element;
                },
            };

            return [virtualizedNode, !canVirtualize || nodeIsInViewport, nodeHeight];
        })
        .map<[VirtualizationItem<D>, boolean, number]>(([vNode, isVisible, nodeHeight], index) => {
            // We loop a second time to show any node between visible nodes, since this information is not known until
            // we ran this loop once. This double loop should have no impact on performance.
            const nodeIsBetweenVisibleNodes = index > firstVisibleNode && index < lastVisibleNode;

            return [vNode, isVisible || nodeIsBetweenVisibleNodes, nodeHeight];
        });

    extendVisibleChunkEdge(result, firstVisibleNode, -1, edgeVirtualWindow);
    extendVisibleChunkEdge(result, lastVisibleNode, 1, edgeVirtualWindow);
    return result;
}

function extendVisibleChunkEdge(
    elements: Array<[VirtualizationItem, boolean, number]>,
    edgeIndex: number,
    direction: -1 | 1,
    distancePx: number
) {
    let left = distancePx;
    let index = edgeIndex + direction;
    let node = elements[index];
    while (node) {
        // @ts-ignore
        elements[index][1] = true;

        left = left - node[2];
        if (left < 0) {
            break;
        }
        index = index + direction;
        node = elements[index];
    }
}

/**
 * Quick comparaison between 2 sets.
 */
function areSetEquals<T>(as: Set<T>, bs: Set<T>): boolean {
    if (as.size !== bs.size) {
        return false;
    }
    for (const a of as) {
        if (!bs.has(a)) {
            return false;
        }
    }
    return true;
}

function hasMarker(element: Element, selector: string): boolean {
    return element.matches(selector) || Boolean(element.querySelector(selector));
}

function isVirtualContentLoading(element: Element): boolean {
    return hasMarker(element, '[data-virtual-loading="true"]');
}

function hasVirtualContentLoaded(element: Element): boolean {
    return hasMarker(element, '[data-virtual-loaded="true"]');
}

function isVirtualizedPlaceholder(element: Element): boolean {
    return element.hasAttribute('data-virtual');
}
